import './scannedData.less'
import {
  ACTION_SCANNED_DATA_GRAPH_FETCH,
  fetchScannedDataWidgets,
  fetchScannedDataCount,
  resetWidgets,
  ScannedDataGraphItem,
  ScannedDataParams,
  SCANNED_RANGE,
  DocumentsScannedParams,
  ACTION_SCANNED_DATA_COUNT_FETCH
} from './scannedDataSlice'
import {
  ACTION_ATTRIBUTE_INSTANCES_COUNT_FETCH,
  fetchAttributeInstancesCount,
  IGetAttributeInstancesCountParams
} from '../attributes/attributesSlice'
import { RootState } from '../../rootReducer'
import {
  DATA_SOURCE_ID,
  DATA_SOURCE_TYPE,
  DATA_SOURCE_TYPES,
  FILTER_ATTRIBUTE_HAS_INSTANCES,
  FILTER_SENSITIVITY_MULTIPLE,
  FILTER_SENSITIVITY_VALUES,
  URL_ATTRIBUTES,
  URL_DATA_SOURCES,
  URL_INSIGHTS
} from '../../constants'
import useUrlContext from '../../hooks/useUrlContext'
import ChartBar from '../../components/ChartBarSimple/chartBarSimple'
import { formatDate, formatTime } from '../../utils/timeUtil'
import { humanDiskSize } from '../../utils/mathUtils'
import { getShowLoader } from '../../reducers/requestReducer'
import LoaderView from '../../components/Loader/loader'
import { changeUrl, getUpdatedUrl } from '../../utils/urlUtil'
import ChartArea from '../../components/ChartArea/chartArea'
import { mockDsScannedDataWidgetsData } from '../dashboards/renderer/utils/widgetMockdata'
import { getUniqueWidgetIndex, UNIQUE_WIDGET_INDEX } from '../dashboards/mainDashboardv2/constants'
import { getDataSourceBasePath } from '../../utils/dataSourceUtil'
import { connect } from 'react-redux'
import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { Link } from 'react-router-dom'
import dayjs from 'dayjs'
import { Typography } from '@lightbeamai/design-system'
interface OwnProps {
  useMockData?: boolean
  parentUrl?: string
  widgetDatasourceId?: string
  widgetDashboardId?: string
  widgetDatasourceType?: DATA_SOURCE_TYPES
}

interface StateProps {
  attributes?: ScannedDataGraphItem[]
  documents?: ScannedDataGraphItem[]
  totalDataScanned?: number
  showLoader: boolean
  totalAttributeInstances?: number
}

interface DispatchProps {
  fetchScannedDataWidgets: (params: ScannedDataParams & { [UNIQUE_WIDGET_INDEX]: string }) => void
  fetchAttributeInstancesCount: (
    params: IGetAttributeInstancesCountParams & { [UNIQUE_WIDGET_INDEX]: string }
  ) => void
  fetchScannedDataCount: (
    params: DocumentsScannedParams & { [UNIQUE_WIDGET_INDEX]: string }
  ) => void
  resetWidgets: (widgetIndex: string) => void
}

type ScannedDataWidgetsComponentProps = StateProps & DispatchProps & OwnProps

const ScannedDataWidgets = (props: ScannedDataWidgetsComponentProps) => {
  const {
    attributes = [],
    documents = [],
    totalDataScanned = 0,
    showLoader,
    totalAttributeInstances = 0,
    fetchScannedDataCount,
    fetchScannedDataWidgets,
    fetchAttributeInstancesCount,
    resetWidgets,
    parentUrl = '',
    useMockData = false,
    widgetDatasourceId,
    widgetDashboardId,
    widgetDatasourceType
  } = props

  const intl = useIntl()
  const context = useUrlContext()
  const datasourceId = widgetDatasourceId || context.contextFilters[DATA_SOURCE_ID] || ''
  const widgetIndex = getUniqueWidgetIndex(widgetDashboardId, widgetDatasourceId)
  const [range, setRange] = useState<SCANNED_RANGE>(SCANNED_RANGE.week)
  const barToolTipFormatter = (val) => humanDiskSize(val)
  useEffect(() => {
    if (useMockData) return
    fetchScannedDataCount({
      ...(datasourceId ? { datasourceId } : {}),
      [UNIQUE_WIDGET_INDEX]: widgetIndex
    })
  }, [datasourceId, fetchScannedDataCount, useMockData, widgetIndex])

  useEffect(() => {
    if (useMockData) return
    const instancesCountParams = {
      ...(datasourceId ? { datasourceIds: [datasourceId] } : {}),
      [UNIQUE_WIDGET_INDEX]: widgetIndex
    }
    fetchAttributeInstancesCount(instancesCountParams)
  }, [datasourceId, fetchAttributeInstancesCount, useMockData, widgetIndex])

  useEffect(() => {
    if (useMockData) return
    fetchScannedDataWidgets({
      datasourceId,
      range,
      [UNIQUE_WIDGET_INDEX]: widgetIndex
    })
  }, [datasourceId, range, fetchScannedDataWidgets, useMockData, widgetIndex])

  useEffect(() => {
    return () => resetWidgets(widgetIndex)
  }, [resetWidgets, widgetIndex])

  const docsSorted = JSON.parse(JSON.stringify(documents)).sort((docFirst, docSecond) =>
    dayjs(docFirst.timestamp).diff(dayjs(docSecond.timestamp))
  )
  const documentsChartData = docsSorted.map((item) => ({
    name:
      range === SCANNED_RANGE.week ? formatDate(item.timestamp, true) : formatTime(item.timestamp),
    value: item.value
  }))

  const attributesSorted = JSON.parse(JSON.stringify(attributes)).sort((attrFirst, attrSecond) =>
    dayjs(attrFirst.timestamp).diff(dayjs(attrSecond.timestamp))
  )
  const attributesChartDate = attributesSorted.map((item) => ({
    name:
      range === SCANNED_RANGE.week ? formatDate(item.timestamp, true) : formatTime(item.timestamp),
    value: item.value
  }))

  const pathBaseString = getDataSourceBasePath(widgetDatasourceType, datasourceId, context.pathname)

  const handleGoToDataSourcesPage = () => !datasourceId && changeUrl(URL_DATA_SOURCES)
  const baseUrl = parentUrl ? parentUrl : pathBaseString
  const attributeLink = getUpdatedUrl({
    pathName: baseUrl + URL_ATTRIBUTES,
    pageName: URL_ATTRIBUTES,
    ...(parentUrl === URL_INSIGHTS && {
      pageQueryParams: {
        [FILTER_ATTRIBUTE_HAS_INSTANCES]: 'Yes',
        [FILTER_SENSITIVITY_MULTIPLE]: FILTER_SENSITIVITY_VALUES.high
      }
    }),
    queryParams: {
      ...(widgetDatasourceType && { [DATA_SOURCE_TYPE]: widgetDatasourceType }),
      ...(datasourceId && { [DATA_SOURCE_ID]: datasourceId })
    },
    resetPageQuery: true
  })
  const handleGoToDataAttributesPage = () => changeUrl(attributeLink)
  const documentsLabelFormatter = (value) => humanDiskSize(value)
  const attributesLabelFormatter = (value) => intl.formatNumber(value, { notation: 'compact' })

  return (
    <div
      className="scanned-data-widgets lb-widget-card xs-p-16"
      data-test-id="scanneddata-scanned-data-widgets"
    >
      <div className="header-wrapper">
        <div className="dashboard-container-title">
          <Typography variant="h6" color="primary">
            {intl.formatMessage({ id: 'scannedData.widget.title' })}
          </Typography>
        </div>
        <div className="date-range-container">
          <button
            className={`btn day xs-py-4 xs-px-16 ${range === SCANNED_RANGE.day ? 'active' : ''}`}
            onClick={() => setRange(SCANNED_RANGE.day)}
          >
            {intl.formatMessage({ id: 'scannedData.widget.day' })}
          </button>
          <button
            className={`btn week xs-py-4 xs-px-16 ${range === SCANNED_RANGE.week ? 'active' : ''}`}
            onClick={() => setRange(SCANNED_RANGE.week)}
          >
            {intl.formatMessage({ id: 'scannedData.widget.week' })}
          </button>
        </div>
      </div>
      <div className="charts">
        <div className="chart documents">
          <div className="title">
            <Typography as="span" color="primary" variant="basePlus6" type="strong">
              {humanDiskSize(totalDataScanned)}
            </Typography>
            <Typography as="p" variant="baseMinus2" type="normal" color="primary">
              {intl.formatMessage({ id: 'scannedData.widget.dataScanned' })}
            </Typography>
          </div>
          <div>
            <ChartBar
              onClick={handleGoToDataSourcesPage}
              data={documentsChartData}
              yLabelFormatter={documentsLabelFormatter}
              YTickFormatter={documentsLabelFormatter}
              barColor="var(--color-scanned-data-documents-bar)"
              className="xs-mt-16"
              toolTipFormatter={barToolTipFormatter}
              xAxisAngle={0}
              cartesianGridHorrizontal={true}
            />
            <LoaderView size="small" showLoader={showLoader} />
          </div>
        </div>
        <div className="chart xs-mt-0 attributes">
          <Link to={attributeLink}>
            <div className="title">
              <Typography as="span" color="primary" variant="basePlus6" type="strong">
                {intl.formatNumber(totalAttributeInstances, { notation: 'compact' })}
              </Typography>
              <Typography as="p" variant="baseMinus2" type="normal" color="primary">
                {intl.formatMessage({ id: 'scannedData.widget.attributesFound' })}
              </Typography>
            </div>
            <div>
              <ChartArea
                onClick={handleGoToDataAttributesPage}
                data={attributesChartDate}
                yLabelFormatter={attributesLabelFormatter}
                areaColor="var(--color-scanned-data-attributes-bar)"
                className="xs-mt-16"
                cartesianGridHorrizontal={true}
              />
              <LoaderView size="small" showLoader={showLoader} />
            </div>
          </Link>
        </div>
      </div>
    </div>
  )
}

const mapStateToProps = (state: RootState, ownProps: OwnProps): StateProps => ({
  attributes: ownProps?.useMockData
    ? mockDsScannedDataWidgetsData.attributes
    : state.scannedData[
        getUniqueWidgetIndex(ownProps.widgetDashboardId, ownProps.widgetDatasourceId)
      ]?.graphAttributes,
  documents: ownProps?.useMockData
    ? mockDsScannedDataWidgetsData.documents
    : state.scannedData[
        getUniqueWidgetIndex(ownProps.widgetDashboardId, ownProps.widgetDatasourceId)
      ]?.graphDocuments,
  totalDataScanned: ownProps?.useMockData
    ? mockDsScannedDataWidgetsData.totalDataScanned
    : state.scannedData[
        getUniqueWidgetIndex(ownProps.widgetDashboardId, ownProps.widgetDatasourceId)
      ]?.totalDataScanned,
  totalAttributeInstances: ownProps?.useMockData
    ? mockDsScannedDataWidgetsData.totalAttributeInstances
    : state.attributes.attributes.widgetInstancesCount?.[
        getUniqueWidgetIndex(ownProps.widgetDashboardId, ownProps.widgetDatasourceId)
      ],
  showLoader:
    getShowLoader(ACTION_SCANNED_DATA_GRAPH_FETCH) ||
    getShowLoader(ACTION_ATTRIBUTE_INSTANCES_COUNT_FETCH) ||
    getShowLoader(ACTION_SCANNED_DATA_COUNT_FETCH)
})

const mapDispatchToProps: DispatchProps = {
  fetchScannedDataWidgets,
  fetchAttributeInstancesCount,
  fetchScannedDataCount,
  resetWidgets
}

export default connect<StateProps, DispatchProps, OwnProps, RootState>(
  mapStateToProps,
  mapDispatchToProps
)(ScannedDataWidgets)
